/**
 * v1.14.69.1-cyber-multi-pdf-snapshot-fix — D&O Wizard 11-step
 *
 * FASE 5.1 features:
 *  - Pattern boolean Sì/No esplicito (radio button group) con stato null = non scelto
 *  - description_field condizionale obbligatoria quando boolean=true
 *  - Bilanci → tabella (desktop) + cards collapsabili (mobile)
 *  - Container overflow-x-hidden + currency input con prefix INSIDE
 *  - Badge pdf_ref + description italic verbatim
 */
import React, { useMemo, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { Loader2, Shield, Sparkles, AlertCircle, Download,
         ChevronDown, ChevronUp } from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import QuestionarioIntroBanner from "@/components/QuestionarioIntroBanner";
import { WizardPreExportSummary } from "@/components/WizardPreExportSummary";
import IVASSComplianceCheckboxes, { buildMupVisioneMeta, isIvassComplianceOk } from "@/components/IVASSComplianceCheckboxes";
import ConsensiGranulari, { buildConsensiGranulariMeta, isConsensiOk } from "@/components/ConsensiGranulari";
import ContattoPreferenzeBlock from "@/components/ContattoPreferenzeBlock";
import AcceptAllInformative from "@/components/AcceptAllInformative";
import { WizardDownloadPanel } from "@/components/wizard/WizardDownloadPanel";
import { ControllateTableInput } from "@/components/wizard/ControllateTableInput";
import { ArrayField } from "@/components/wizard/ArrayField";
import { parseLegacyArrayString } from "@/lib/legacyArrayParse";
import { ContractArticlesModal } from "@/components/wizard/ContractArticlesModal";
import { IvassConsentsStepUnified, registerConsentsBulk } from "@/components/wizard/IvassConsentsStepUnified";
import { computeAssuntiviWarnings, findPlaceholders } from "@/lib/assuntivi_warnings";
import { StepProgress, FormNavigation } from "@/components/StepProgress";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import useDOWizardState from "./useDOWizardState";
import { enrichLabelWithYear, exerciseYear } from "@/lib/exerciseYear";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/do`;

/* ------------------------------------------------------------------ */
/* Currency input with prefix INSIDE                                   */
/* ------------------------------------------------------------------ */
function CurrencyInput({ tid, value, onChange, className = "" }) {
  const [display, setDisplay] = useState("");
  useEffect(() => {
    if (value === "" || value == null) { setDisplay(""); return; }
    const n = parseFloat(value);
    if (Number.isNaN(n)) { setDisplay(String(value)); return; }
    // format with thousand separator '.'
    setDisplay(n.toLocaleString("it-IT", { maximumFractionDigits: 2 }));
  }, [value]);
  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^\d,.-]/g, "");
    // accept european format: 1.500.000,50
    // normalize → 1500000.50
    const normalized = raw.replace(/\./g, "").replace(",", ".");
    if (normalized === "" || normalized === "-") {
      setDisplay(raw); onChange(""); return;
    }
    const n = parseFloat(normalized);
    if (!Number.isNaN(n)) {
      setDisplay(raw);
      onChange(n);
    } else {
      setDisplay(raw);
    }
  };
  return (
    <div className={`relative ${className}`}>
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5E51] text-sm pointer-events-none">€</span>
      <Input type="text" inputMode="decimal" data-testid={tid}
              value={display}
              onChange={handleChange}
              placeholder="0,00"
              className="rounded-xl border-[#E5E2DB] h-11 pl-7" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Boolean Sì/No radio + optional inline description field             */
/* ------------------------------------------------------------------ */
function YesNoRadio({ tid, value, onChange, disabled = false }) {
  const isYes = value === true;
  const isNo = value === false;
  const base = "px-4 py-2 rounded-full text-sm font-medium transition-colors border";
  return (
    <div className="flex gap-2" data-testid={`${tid}-radio-group`}>
      <button type="button"
              data-testid={`${tid}-yes`}
              disabled={disabled}
              onClick={() => onChange(true)}
              aria-pressed={isYes}
              className={`${base} ${
                isYes
                  ? "bg-[#163321] text-white border-[#163321]"
                  : "bg-white text-[#163321] border-[#163321]/40 hover:bg-[#163321]/5"}`}>
        Sì
      </button>
      <button type="button"
              data-testid={`${tid}-no`}
              disabled={disabled}
              onClick={() => onChange(false)}
              aria-pressed={isNo}
              className={`${base} ${
                isNo
                  ? "bg-[#7A1D1D] text-white border-[#7A1D1D]"
                  : "bg-white text-[#7A1D1D] border-[#7A1D1D]/40 hover:bg-[#7A1D1D]/5"}`}>
        No
      </button>
    </div>
  );
}

function BooleanField({ sezKey, campoKey, campo, value, sectionData, sezCampi,
                        formData, onChange, setField }) {
  const tid = `do-wizard-field-${sezKey}-${campoKey}`;
  const [articlesModalOpen, setArticlesModalOpen] = React.useState(false);
  const isYes = value === true;
  const isNo = value === false;
  const isUnset = value === null || value === undefined;
  // resolve description_field
  let descField = null;
  let descSezKey = sezKey;
  let descCampoKey = null;
  if (campo.requires_description_if_yes && campo.description_field) {
    const parts = String(campo.description_field).split(".");
    if (parts.length === 2) { descSezKey = parts[0]; descCampoKey = parts[1]; }
    else { descCampoKey = parts[0]; }
    descField = (formData[descSezKey] || {})[descCampoKey];
  }
  // FASE 5.7 — se il child description è required=true a livello schema, viene già
  // renderizzato standalone da SectionBlock. Evitiamo il duplicato inline nel BooleanField.
  const descChildCampo = descCampoKey
    ? (descSezKey === sezKey ? (sezCampi || {})[descCampoKey] : null)
    : null;
  const skipInlineDesc = !!(descChildCampo && descChildCampo.required);
  const descMissing = isYes && campo.requires_description_if_yes
                       && !skipInlineDesc
                       && (!descField || String(descField).trim().length < 3);
  const descFieldType = campo.description_field_type || "textarea";
  const descLabel = campo.description_if_yes_label || "Specificare i dettagli";
  // FASE 5.7 — handler che ripulisce i child quando l'utente passa Yes → No/null.
  // Evita zombie data che potrebbe causare validation stale o dati fantasma nel PDF.
  const handleChange = (newVal) => {
    if (value === true && newVal !== true) {
      // 1. reset del description_field (solo se non è un campo standalone required)
      if (campo.requires_description_if_yes && descCampoKey && !skipInlineDesc) {
        setField(descSezKey, descCampoKey, undefined);
      }
      // 2. reset di tutti i child con conditional_on === campoKey (nella stessa sezione)
      if (sezCampi) {
        Object.entries(sezCampi).forEach(([k, c]) => {
          if (c && c.conditional_on === campoKey) {
            setField(sezKey, k, undefined);
          }
        });
      }
    }
    onChange(newVal);
  };
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5E2DB] hover:border-[#163321]/30 transition-colors"
         data-testid={`${tid}-wrap`}>
      <div className="flex flex-col gap-3">
        <div className="flex-1 min-w-0">
          <span className="block text-sm text-[#0F1F14] font-medium leading-relaxed whitespace-pre-wrap break-words">
            {enrichLabelWithYear(campo.label)}
            {campo.required && <span className="text-red-600 ml-1">*</span>}
          </span>
          {campo.pdf_ref && (
            <span className="inline-block mt-1 text-[10px] text-[#7A8E83] bg-[#F5F4F0] rounded px-1.5 py-0.5"
                  data-testid={`${tid}-pdfref`}
                  title={`Riferimento PDF: ${campo.pdf_ref}`}>
              {campo.pdf_ref}
            </span>
          )}
          {campo.description && (
            <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap break-words mt-1.5 italic"
               data-testid={`${tid}-description`}>
              {campo.description}
            </p>
          )}
          {campo.help_link === "cond_gen_articles_modal" && (
            <>
              <button
                type="button"
                onClick={() => setArticlesModalOpen(true)}
                data-testid={`${tid}-read-articles-btn`}
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#163321] hover:text-[#0F1F14] font-medium underline decoration-dotted underline-offset-2"
              >
                📄 Leggi il testo completo dei 13 articoli
              </button>
              <ContractArticlesModal
                open={articlesModalOpen}
                onOpenChange={setArticlesModalOpen} />
            </>
          )}
        </div>
        <YesNoRadio tid={tid} value={value}
                     onChange={handleChange} />
        {isUnset && campo.required && (
          <p className="text-xs text-amber-700"
              data-testid={`${tid}-unset-warning`}>
            Seleziona Sì o No per procedere.
          </p>
        )}
        {isYes && campo.requires_description_if_yes && descCampoKey && !skipInlineDesc && (
          <div className="mt-1 pl-3 border-l-2 border-[#163321]/30"
               data-testid={`${tid}-desc-wrap`}>
            <Label className="block text-sm text-[#0F1F14] mb-1.5">
              {descLabel}
              <span className="text-red-600 ml-1">*</span>
            </Label>
            <FieldRenderInner sezKey={descSezKey}
                              campoKey={descCampoKey}
                              campo={{ ...(formData?.__schema_helper?.[descSezKey]?.[descCampoKey] || {}),
                                       type: descFieldType,
                                       placeholder: descLabel }}
                              value={descField}
                              onChange={(v) => setField(descSezKey, descCampoKey, v)}
                              hideLabel
                              dataTestId={`${tid}-desc-input`} />
            {descMissing && (
              <p className="text-xs text-red-600 mt-1"
                  data-testid={`${tid}-desc-missing`}>
                Specifica i dettagli (almeno 3 caratteri).
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Generic field renderer (inner — no label wrap)                      */
/* ------------------------------------------------------------------ */
function FieldRenderInner({ sezKey, campoKey, campo, value, onChange,
                            hideLabel = false, dataTestId }) {
  const tid = dataTestId || `do-wizard-field-${sezKey}-${campoKey}`;
  const v = value ?? "";
  const labelNode = hideLabel ? null : (
    <Label className="block mb-1.5">
      <span className="block text-sm text-[#0F1F14] font-medium leading-relaxed whitespace-pre-wrap break-words">
        {enrichLabelWithYear(campo.label)}
        {campo.required && <span className="text-red-600 ml-1">*</span>}
      </span>
      {campo.pdf_ref && (
        <span className="inline-block mt-1 text-[10px] text-[#7A8E83] bg-[#F5F4F0] rounded px-1.5 py-0.5"
              data-testid={`${tid}-pdfref`}
              title={`Riferimento PDF: ${campo.pdf_ref}`}>
          {campo.pdf_ref}
        </span>
      )}
      {campo.description && (
        <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap break-words mt-1.5 italic"
           data-testid={`${tid}-description`}>
          {campo.description}
        </p>
      )}
    </Label>
  );

  if (campo.type === "controllate_table") {
    // Fase 4.9 — tabella strutturata Reale Mutua 2.1 (5 righe × 5 colonne)
    return (
      <div>
        {labelNode}
        <div className="mt-1.5">
          <ControllateTableInput
            value={value}
            onChange={onChange}
            maxRows={campo.max_rows || 5} />
        </div>
      </div>
    );
  }

  if (campo.type === "array" && campo.item_schema) {
    // FASE 10.4 — Array-of-objects (Amministratori, Sindaci, Soci >15%, Persone RC Amm).
    // Retrocompat: se il valore è ancora stringa multiline legacy, la parsiamo al volo
    // in array di oggetti usando `serialize_as_lines_format`.
    let arrValue = value;
    if (typeof value === "string") {
      arrValue = parseLegacyArrayString(value, campo.item_schema,
                                          campo.serialize_as_lines_format || "");
      // Notifichiamo il parent perché lo state resti coerente (array)
      // solo se abbiamo effettivamente parsato qualcosa di non-vuoto.
      if (arrValue.length > 0 && arrValue !== value) {
        // Deferred to avoid setState-during-render warnings
        setTimeout(() => onChange(arrValue), 0);
      }
    }
    if (!Array.isArray(arrValue)) arrValue = [];
    return (
      <div>
        {labelNode}
        <div className="mt-1.5">
          <ArrayField
            label=""
            value={arrValue}
            itemSchema={campo.item_schema}
            maxItems={campo.max_items || 10}
            minItems={campo.min_items || 0}
            onChange={onChange}
            testId={tid} />
        </div>
      </div>
    );
  }

  if (campo.type === "textarea") {
    return (
      <div>
        {labelNode}
        <Textarea data-testid={tid} value={v} rows={3}
                   onChange={(e) => onChange(e.target.value)}
                   placeholder={campo.placeholder}
                   className="rounded-xl border-[#E5E2DB] mt-1.5 max-w-full" />
      </div>
    );
  }
  if (campo.type === "select" && Array.isArray(campo.options)) {
    return (
      <div>
        {labelNode}
        <Select value={v || ""} onValueChange={onChange}>
          <SelectTrigger className="rounded-xl border-[#E5E2DB] h-11 mt-1.5" data-testid={tid}>
            <SelectValue placeholder="Seleziona" />
          </SelectTrigger>
          <SelectContent>
            {campo.options.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (campo.type === "currency") {
    return (
      <div>
        {labelNode}
        <div className="mt-1.5">
          <CurrencyInput tid={tid} value={v} onChange={onChange} />
        </div>
      </div>
    );
  }
  const inputType = campo.type === "date" ? "date"
                  : campo.type === "email" ? "email"
                  : campo.type === "tel" ? "tel"
                  : campo.type === "numeric" ? "number"
                  : "text";
  return (
    <div>
      {labelNode}
      <Input type={inputType} data-testid={tid} value={v}
              onChange={(e) => onChange(e.target.value)}
              placeholder={campo.placeholder}
              className="rounded-xl border-[#E5E2DB] h-11 mt-1.5 max-w-full" />
    </div>
  );
}

function FieldRender({ sezKey, campoKey, campo, value, formData, sectionData, sezCampi,
                       onChange, setField }) {
  if (campo.type === "boolean") {
    return (
      <BooleanField sezKey={sezKey} campoKey={campoKey} campo={campo}
                     value={value} sectionData={sectionData} sezCampi={sezCampi}
                     formData={formData}
                     onChange={onChange} setField={setField} />
    );
  }
  return (
    <FieldRenderInner sezKey={sezKey} campoKey={campoKey} campo={campo}
                       value={value} onChange={onChange} />
  );
}

/* ------------------------------------------------------------------ */
/* Bilanci table (desktop) + cards (mobile)                            */
/* ------------------------------------------------------------------ */
function BilanciSection({ sez, formData, setField }) {
  const sectionData = formData.bilanci || {};
  const voci = [
    { key: "anno", label: "Anno", type: "numeric" },
    { key: "fatturato_eur", label: "Fatturato", type: "currency" },
    { key: "valore_produzione_eur", label: "Valore della Produzione", type: "currency" },
    { key: "patrimonio_netto_eur", label: "Patrimonio Netto", type: "currency" },
    { key: "totale_attivo_eur", label: "Totale Attivo", type: "currency" },
    { key: "risultato_ante_imposte_eur", label: "Risultato Ante Imposte", type: "currency" },
  ];
  const exercises = [
    { idx: 1, label: `Esercizio 1 — Ultimo (${exerciseYear(1)})` },
    { idx: 2, label: `Esercizio 2 — Penultimo (${exerciseYear(2)})` },
    { idx: 3, label: `Esercizio 3 — Terz'ultimo (${exerciseYear(3)}, opzionale)` },
  ];
  const [collapsed, setCollapsed] = useState({ 1: false, 2: true, 3: true });
  // extra D12 boolean campi sotto la tabella
  const extra = ["patrimonio_netto_positivo_si_no",
                  "perdita_oltre_20pct_pn_si_no",
                  "rapporto_attivo_debiti_oltre_080"];
  return (
    <div className="space-y-5" data-testid="do-wizard-section-bilanci">
      <div>
        <h2 className="font-heading text-xl text-[#0F1F14] font-medium mb-1 whitespace-pre-wrap leading-snug break-words">
          {sez.label}
        </h2>
        <p className="text-sm text-[#4A5E51]">{sez.subtitle}</p>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto"
           data-testid="bilanci-table-desktop">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-[#F5F4F0]">
              <th className="p-2 text-left font-medium text-[#163321] border-b border-[#E5E2DB]">
                Voce
              </th>
              {exercises.map((e) => (
                <th key={e.idx}
                    className="p-2 text-left font-medium text-[#163321] border-b border-[#E5E2DB] min-w-[150px]">
                  {e.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {voci.map((v) => (
              <tr key={v.key} className="border-b border-[#E5E2DB]">
                <td className="p-2 text-[#0F1F14] font-medium align-middle">
                  {v.label}
                </td>
                {exercises.map((e) => {
                  const key = `e${e.idx}_${v.key}`;
                  return (
                    <td key={e.idx} className="p-2 align-middle">
                      {v.type === "currency" ? (
                        <CurrencyInput
                          tid={`do-wizard-bilanci-e${e.idx}-${v.key}`}
                          value={sectionData[key]}
                          onChange={(val) => setField("bilanci", key, val)} />
                      ) : (
                        <Input type="number"
                                inputMode="numeric"
                                data-testid={`do-wizard-bilanci-e${e.idx}-${v.key}`}
                                value={sectionData[key] ?? ""}
                                onChange={(ev) => setField("bilanci", key, ev.target.value)}
                                className="rounded-lg border-[#E5E2DB] h-10" />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid="bilanci-cards-mobile">
        {exercises.map((e) => (
          <div key={e.idx}
               className="rounded-xl border border-[#E5E2DB] bg-white overflow-hidden">
            <button type="button"
                    data-testid={`bilanci-card-toggle-${e.idx}`}
                    onClick={() => setCollapsed((c) => ({ ...c, [e.idx]: !c[e.idx] }))}
                    className="w-full flex items-center justify-between p-3 bg-[#F5F4F0] text-left">
              <span className="font-medium text-[#163321]">{e.label}</span>
              {collapsed[e.idx]
                ? <ChevronDown className="w-4 h-4" />
                : <ChevronUp className="w-4 h-4" />}
            </button>
            {!collapsed[e.idx] && (
              <div className="p-3 space-y-3">
                {voci.map((v) => {
                  const key = `e${e.idx}_${v.key}`;
                  return (
                    <div key={v.key}>
                      <Label className="block text-xs text-[#4A5E51] mb-1">
                        {v.label}
                      </Label>
                      {v.type === "currency" ? (
                        <CurrencyInput
                          tid={`do-wizard-bilanci-e${e.idx}-${v.key}-mob`}
                          value={sectionData[key]}
                          onChange={(val) => setField("bilanci", key, val)} />
                      ) : (
                        <Input type="number"
                                inputMode="numeric"
                                data-testid={`do-wizard-bilanci-e${e.idx}-${v.key}-mob`}
                                value={sectionData[key] ?? ""}
                                onChange={(ev) => setField("bilanci", key, ev.target.value)}
                                className="rounded-lg border-[#E5E2DB] h-10" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-500 italic">
        Esercizio 1 = ultimo esercizio finanziario completo. Esercizio 2 = penultimo (approvato o meno). Esercizio 3 = terz'ultimo se applicabile (Lloyd's 4.A).
      </p>

      {/* Extra D12/D13 boolean below */}
      <div className="space-y-3">
        {extra.map((k) => {
          const campo = sez.campi[k];
          if (!campo) return null;
          return (
            <FieldRender key={k}
                          sezKey="bilanci" campoKey={k} campo={campo}
                          value={sectionData[k]}
                          formData={formData} sectionData={sectionData}
                          sezCampi={sez.campi}
                          onChange={(v) => setField("bilanci", k, v)}
                          setField={setField} />
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Generic section block                                               */
/* ------------------------------------------------------------------ */
function SectionBlock({ sezKey, sez, formData, setField }) {
  const sectionData = formData[sezKey] || {};
  if (sez.render === "bilanci_table" || sezKey === "bilanci") {
    return <BilanciSection sez={sez} formData={formData} setField={setField} />;
  }
  // FASE 5.7 — hidden description child (managed by parent boolean) — evita doppio render.
  // ECCEZIONE: se il child ha `required: true` a livello schema, resta visibile standalone
  // (perché è un campo autonomo che raddoppia come description_field, es. data_costituzione).
  const hiddenByParent = new Set();
  Object.values(sez.campi).forEach((c) => {
    if (c.requires_description_if_yes && c.description_field) {
      const parts = String(c.description_field).split(".");
      const localKey = parts.length === 2 && parts[0] === sezKey ? parts[1] : parts[0];
      if (!localKey) return;
      const childCampo = sez.campi[localKey];
      if (childCampo && childCampo.required) return; // child required → non nascondere
      hiddenByParent.add(localKey);
    }
  });
  return (
    <div className="space-y-5" data-testid={`do-wizard-section-${sezKey}`}>
      <div>
        <h2 className="font-heading text-xl text-[#0F1F14] font-medium mb-1 whitespace-pre-wrap leading-snug break-words">
          {sez.label}
        </h2>
        {sez.subtitle && (
          <p className="text-sm text-[#4A5E51]">{sez.subtitle}</p>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Object.entries(sez.campi).map(([campoKey, campo]) => {
          if (campo.is_cyber_prompt) return null;
          if (hiddenByParent.has(campoKey)) return null;
          // Fase 4.9 — nascondi textarea legacy `controllate_partecipate_lista`
          // se il nuovo array `controllate` è renderizzato (ha type dedicato)
          // e non c'è già un valore legacy da mostrare. Retrocompat: se una
          // bozza vecchia ha ancora la stringa e nessun array, mostrala per
          // permetterne l'editing manuale.
          if (campo.deprecated) {
            const hasArr = Array.isArray(sectionData.controllate)
                            && sectionData.controllate.length > 0;
            const legacyVal = sectionData[campoKey];
            const legacyHasContent = typeof legacyVal === "string"
                                       && legacyVal.trim().length > 0;
            if (hasArr || !legacyHasContent) return null;
          }
          // legacy conditional_on still respected for non-boolean parent cases
          if (campo.conditional_on) {
            const parentVal = sectionData[campo.conditional_on];
            if (parentVal !== true) return null;
          }
          const isLongLabel = typeof campo.label === "string" && campo.label.length > 80;
          const hasDescription = Boolean(campo.description);
          const isFullWidth = campo.type === "textarea" || campo.type === "boolean"
                              || campo.type === "controllate_table"
                              || campo.type === "array"
                              || isLongLabel || hasDescription;
          return (
            <div key={campoKey} className={isFullWidth ? "sm:col-span-2" : ""}>
              <FieldRender sezKey={sezKey} campoKey={campoKey} campo={campo}
                            value={sectionData[campoKey]}
                            formData={formData} sectionData={sectionData}
                            sezCampi={sez.campi}
                            onChange={(v) => setField(sezKey, campoKey, v)}
                            setField={setField} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Contacts + cyber prompt                                             */
/* ------------------------------------------------------------------ */
function ContactsBlock({ contactData, setContactData }) {
  return (
    <div className="bg-[#FDF8E8] border border-[#D9C49A] rounded-2xl p-5 mb-5"
          data-testid="do-wizard-contacts">
      <h3 className="font-heading text-base text-[#0F1F14] font-medium mb-1 flex items-center gap-2">
        <Shield className="w-4 h-4 text-[#163321]" />
        I tuoi contatti
      </h3>
      <p className="text-xs text-[#4A5E51] mb-4">
        Indica come ricontattarti. <span className="text-red-600">*</span> obbligatorio (nome o email).
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          ["client_name", "Nome e cognome *", "text", "do-wizard-contact-name"],
          ["client_email", "Email *", "email", "do-wizard-contact-email"],
          ["client_telefono", "Telefono", "tel", "do-wizard-contact-tel"],
          ["company_name", "Ragione sociale", "text", "do-wizard-contact-company"],
        ].map(([key, label, type, tid]) => (
          <div key={key}>
            <Label className="text-sm text-[#0F1F14] block mb-1.5">{label}</Label>
            <Input type={type} data-testid={tid}
                    value={contactData[key]}
                    onChange={(e) => setContactData({ ...contactData, [key]: e.target.value })}
                    className="rounded-xl border-[#E5E2DB] h-11 max-w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CyberPromptBlock({ wantsCyber, setWantsCyber }) {
  return (
    <div className="mt-6 p-5 rounded-2xl bg-gradient-to-br from-[#D9C49A]/30 to-[#F0E7C8] border border-[#D9C49A]"
          data-testid="do-wizard-cyber-prompt">
      <div className="flex items-start gap-3">
        <Sparkles className="w-6 h-6 text-[#163321] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg text-[#0F1F14] font-medium">
            Vuoi includere anche una copertura Cyber Risk?
          </h3>
          <p className="text-sm text-[#4A5E51] mt-1 mb-3">
            La D&amp;O protegge gli amministratori. La Cyber Risk protegge l&apos;azienda
            da attacchi informatici, ransomware, perdita dati. Compilando una sola
            richiesta riceverai entrambi i preventivi.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button type="button" data-testid="do-wizard-cyber-yes"
                    onClick={() => setWantsCyber(true)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      wantsCyber === true
                        ? "bg-[#163321] text-white"
                        : "bg-white text-[#163321] border border-[#163321]/40 hover:bg-[#163321]/5"
                    }`}>
              Sì, voglio anche Cyber Risk
            </button>
            <button type="button" data-testid="do-wizard-cyber-no"
                    onClick={() => setWantsCyber(false)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      wantsCyber === false
                        ? "bg-[#163321] text-white"
                        : "bg-white text-[#163321] border border-[#163321]/40 hover:bg-[#163321]/5"
                    }`}>
              No, solo D&amp;O
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step validation: blocca avanti se boolean non sceltO o desc vuota   */
/* ------------------------------------------------------------------ */
const REASON_MESSAGES = {
  "required-empty": "Campo obbligatorio",
  "yes-no-not-chosen": "Seleziona Sì o No",
  "description-required": "Descrizione mancante (min. 3 caratteri)",
};

/**
 * FASE 5.7 — Determina se un campo è VISIBILE nel wizard corrente:
 *  - se `conditional_on` è definito, il campo è visibile solo se il parent === true
 *  - se il campo è description_field di un parent boolean con requires_description_if_yes,
 *    il campo è "logicamente visibile" solo se il parent === true (a meno che
 *    il campo abbia `required: true` a livello schema, nel qual caso è sempre visibile
 *    standalone e va validato normalmente).
 */
function isFieldEffectivelyVisible(sez, campoKey, campo, sectionData) {
  // Regola 1 — conditional_on: parent nella stessa sezione deve essere === true
  if (campo.conditional_on) {
    const parentVal = sectionData[campo.conditional_on];
    if (parentVal !== true) return false;
  }
  // Regola 2 — se è description_field di un parent boolean con requires_description_if_yes
  // e non è required standalone → visibile solo se parent === true.
  if (!campo.required) {
    for (const [parentKey, parentCampo] of Object.entries(sez.campi || {})) {
      if (parentCampo.requires_description_if_yes && parentCampo.description_field) {
        const parts = String(parentCampo.description_field).split(".");
        const tgtSez = parts.length === 2 ? parts[0] : (sez.key || "");
        const tgtKey = parts.length === 2 ? parts[1] : parts[0];
        if (tgtSez === (sez.key || "") && tgtKey === campoKey) {
          // questo campo è description child di parentKey
          if (sectionData[parentKey] !== true) return false;
        }
      }
    }
  }
  return true;
}

function validateStep(sez, formData) {
  if (!sez || !sez.campi) return { valid: true, issues: [] };
  const sectionData = formData[sez.key || ""] || formData[Object.keys(formData)[0]] || {};
  // Iterate campi della sezione
  const issues = [];
  Object.entries(sez.campi).forEach(([k, campo]) => {
    if (campo.is_cyber_prompt) return;
    // FASE 5.7 — skip campi nascosti (parent boolean !== true)
    if (!isFieldEffectivelyVisible(sez, k, campo, sectionData)) return;
    if (campo.required && campo.type !== "boolean") {
      const v = sectionData[k];
      if (v == null || v === "") {
        issues.push({ field: k, reason: "required-empty", label: campo.label });
      }
    }
    if (campo.type === "boolean" && campo.required) {
      const v = sectionData[k];
      if (v == null) issues.push({ field: k, reason: "yes-no-not-chosen", label: campo.label });
    }
    if (campo.type === "boolean"
        && campo.requires_description_if_yes
        && sectionData[k] === true
        && campo.description_field) {
      const parts = String(campo.description_field).split(".");
      const tgtSez = parts.length === 2 ? parts[0] : (sez.key || "");
      const tgtKey = parts.length === 2 ? parts[1] : parts[0];
      const descVal = (formData[tgtSez] || {})[tgtKey];
      // Se il child è required=true a livello schema, sarà validato standalone nella
      // sezione principale (evita doppio errore).
      const tgtCampo = (formData?.__schema_helper?.[tgtSez]?.[tgtKey])
        || ((sez.key === tgtSez) ? (sez.campi || {})[tgtKey] : null);
      const isChildRequiredStandalone = !!(tgtCampo && tgtCampo.required);
      if (!isChildRequiredStandalone
          && (!descVal || String(descVal).trim().length < 3)) {
        issues.push({ field: tgtKey, reason: "description-required",
                       label: campo.description_if_yes_label || "Descrizione" });
      }
    }
  });
  return { valid: issues.length === 0, issues };
}

/* Aggregate validation for ALL steps — used on Submit click */
function validateAllSteps(orderedSteps, formData) {
  const allIssues = [];
  for (const step of orderedSteps) {
    // FASE 13: salta step virtual (IVASS) — validazione gestita separatamente
    if (step.virtual) continue;
    const v = validateStep(step.sez, formData);
    if (!v.valid) {
      v.issues.forEach((i) => {
        allIssues.push({
          ...i,
          stepIdx: orderedSteps.indexOf(step),
          stepTitle: step.title,
        });
      });
    }
  }
  return { valid: allIssues.length === 0, issues: allIssues };
}

/* ------------------------------------------------------------------ */
/* Main wizard                                                         */
/* ------------------------------------------------------------------ */
export default function DOWizard({ openMode = false }) {
  const { token } = useParams();
  const state = useDOWizardState({ token, openMode });
  const {
    loading, errCode, sezioni, wizard, status, clientName,
    formData, contactData, setContactData,
    wantsCyber, setWantsCyber, currentStep, setCurrentStep,
    submitting, setField, submit, clearDraft,
  } = state;
  const [submitErrors, setSubmitErrors] = React.useState(null);
  const [showPreExport, setShowPreExport] = React.useState(false);
  // FASE 13 — IVASS consents unificato: value cumulativo
  const [ivassBulk, setIvassBulk] = React.useState({ accepted: false, document_ids: [] });
  // v1.56 (feb-2026) — Click-wrap IVASS: art. 1892 c.c. + MUP Reg. 40/2018
  const [signedDeclaration, setSignedDeclaration] = React.useState(false);
  const [mupSeen, setMupSeen] = React.useState(false);
  const [mupFilename, setMupFilename] = React.useState(null);
  // v1.66 (Fase 2 Privacy) — Consensi granulari GDPR
  const [consensi, setConsensi] = React.useState({});
  // v1.72.3 — Cross-wizard: preferenze ricontatto + accept-all informative
  const [preferenzeRicontatto, setPreferenzeRicontatto] = React.useState({ canale: "", fascia: "" });
  const handleAcceptAllInformative = () => {
    setSignedDeclaration(true);
    setMupSeen(true);
    setConsensi((prev) => ({ ...prev, marketing: true, profilazione: true, gruppo_partner: true }));
  };
  const sessionHashDO = React.useMemo(
    () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`, []);

  const orderedSteps = useMemo(() => {
    if (!wizard?.steps || !sezioni) return [];
    const base = wizard.steps.map((s) => {
      const sez = sezioni[s.key];
      return sez ? { ...s, sezKey: s.key, sez: { ...sez, key: s.key } } : null;
    }).filter(Boolean);
    // FASE 13 — step virtual IVASS finale (dopo tutte le sezioni)
    base.push({
      key: "__ivass_compliance__",
      sezKey: "__ivass_compliance__",
      title: "Documenti IVASS",
      virtual: true,
      sez: { key: "__ivass_compliance__", campi: {} },
    });
    return base;
  }, [wizard, sezioni]);

  useEffect(() => {
    if (openMode && status?.submitted) clearDraft();
  }, [openMode, status?.submitted, clearDraft]);

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FCFCFA] pt-20 flex items-center justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#163321]" />
        </main>
        <Footer />
      </>
    );
  }
  if (errCode) return <ErrorPage errCode={errCode} />;
  if (status?.submitted && !status?.transmitted) {
    return <ThanksPage clientName={clientName || contactData.client_name || contactData.client_email}
                        openMode={openMode} wantsCyber={wantsCyber}
                        nextCyberUrl={status?.next_cyber_url}
                        downloads={status?.downloads}
                        submissionId={status?.id_pratica} />;
  }
  if (status?.transmitted) return <TransmittedPage token={token} clientName={clientName} />;

  const totalSteps = orderedSteps.length;
  const stepIdx = Math.max(0, currentStep - 1);
  const step = orderedSteps[stepIdx];
  const progress = ((stepIdx + 1) / totalSteps) * 100;
  const stepValidation = step
    ? (step.virtual
        ? { valid: !!ivassBulk.accepted, issues: [] }
        : validateStep(step.sez, formData))
    : { valid: true, issues: [] };
  const canGoNext = stepValidation.valid;

  const handleSubmit = async () => {
    const all = validateAllSteps(orderedSteps, formData);
    if (!all.valid) {
      setSubmitErrors(all.issues);
      toast.error(`Trovati ${all.issues.length} errori. Correggi i campi evidenziati.`);
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { /* ignore */ }
      return;
    }
    // FASE 13 — blocca submit se documentazione IVASS non consultata/accettata
    if (!ivassBulk.accepted) {
      toast.error("Consulta e conferma la documentazione precontrattuale IVASS prima di inviare.");
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { /* ignore */ }
      return;
    }
    // FASE 4.5 — apri schermata pre-export invece di submit diretto
    setSubmitErrors(null);
    setShowPreExport(true);
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { /* ignore */ }
  };

  // Vero submit dopo conferma nella schermata pre-export
  const confirmAndSubmit = async () => {
    if (!isIvassComplianceOk({ signedDeclaration, mupSeen })) {
      toast.error("Conferma dichiarazione veridicità e presa visione MUP per proseguire.");
      return;
    }
    if (!isConsensiOk(consensi)) {
      toast.error("Conferma i consensi obbligatori per procedere.");
      return;
    }
    setShowPreExport(false);
    setSubmitErrors(null);
    const result = await submit(toast, {
      signedDeclaration,
      sessionHash: sessionHashDO,
      mupVisione: buildMupVisioneMeta({ mupSeen, mupFilename }),
      consensiGranulari: buildConsensiGranulariMeta(consensi),
      // v1.72.3 — Preferenze ricontatto (canale + fascia oraria)
      preferenzeRicontatto: (preferenzeRicontatto.canale || preferenzeRicontatto.fascia)
        ? preferenzeRicontatto : null,
    });
    // FASE 13 — registra bulk consents IVASS dopo submit riuscito
    const submissionId = result?.id || result?.id_pratica;
    if (submissionId && ivassBulk.accepted && ivassBulk.document_ids?.length) {
      try {
        await registerConsentsBulk({
          submissionId,
          documentIds: ivassBulk.document_ids,
          module: "do",
        });
      } catch (err) { /* audit non blocca */ }
    }
    if (result && result.ok === false && Array.isArray(result.backendErrors)) {
      const backendIssues = result.backendErrors.map((msg) => ({
        field: "backend",
        reason: "backend",
        label: msg,
        stepIdx: 0,
        stepTitle: orderedSteps[0]?.title || "Anagrafica",
        fromBackend: true,
      }));
      setSubmitErrors(backendIssues);
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { /* ignore */ }
    }
  };

  // Scroll + focus al campo del step target dopo un piccolo delay (attende re-render)
  const jumpToField = (stepIdx, fieldKey) => {
    setCurrentStep(stepIdx + 1);
    setSubmitErrors(null);
    setTimeout(() => {
      try {
        const selectors = [
          `[data-testid$="-${fieldKey}"]`,
          `[data-testid*="${fieldKey}"]`,
          `[name="${fieldKey}"]`,
          `#${fieldKey}`,
        ];
        for (const sel of selectors) {
          const el = document.querySelector(sel);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            if (typeof el.focus === "function") {
              try { el.focus({ preventScroll: true }); } catch (er) { /* ignore */ }
            }
            return;
          }
        }
      } catch (er) { /* ignore */ }
    }, 300);
  };

  return (
    <>
      <Header />
      <Toaster richColors position="top-center" />
      <main className="min-h-screen bg-[#FCFCFA] pt-20 overflow-x-hidden"
            data-testid="do-wizard-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 bg-[#163321]/10 rounded-full flex items-center justify-center">
              <Shield className="w-7 h-7 text-[#163321]" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#0F1F14] mb-2"
                data-testid="do-wizard-title">
              Questionario Assuntivo D&amp;O
            </h1>
            <p className="text-sm text-[#4A5E51] max-w-lg mx-auto">
              Responsabilit&agrave; Civile Amministratori, Sindaci e Dirigenti · Schema unificato 3 compagnie
              {clientName && !openMode && <> · per <strong>{clientName}</strong></>}
            </p>
          </div>

          <QuestionarioIntroBanner ramoRischio="D&O" />

          {/* FASE 4.5 — Schermata pre-export riepilogo & verifica */}
          {showPreExport && (
            <div className="bg-white rounded-2xl border border-[#E5E2DB] p-5 sm:p-7 mb-6"
                  data-testid="do-wizard-pre-export-container">
              <WizardPreExportSummary
                formData={formData}
                validationErrors={[]}
                warnings={computeAssuntiviWarnings(formData)}
                placeholders={findPlaceholders(formData)}
                onProceed={confirmAndSubmit}
                onBack={() => setShowPreExport(false)}
                submitting={submitting}
                canProceed={isIvassComplianceOk({ signedDeclaration, mupSeen }) && isConsensiOk(consensi)}
              />
              {/* v1.72.3 — Preferenze ricontatto (canale + fascia) */}
              <div className="mt-5 pt-5 border-t border-[#E5E2DB]">
                <p className="text-xs uppercase tracking-wide text-[#4A5E51] mb-3">Come preferisci essere ricontattato/a?</p>
                <ContattoPreferenzeBlock
                  value={preferenzeRicontatto}
                  onChange={setPreferenzeRicontatto}
                  testidPrefix="do-ricontatto"
                />
              </div>
              {/* v1.72.3 — Accetta tutte le informative facoltative */}
              <div className="mt-5">
                <AcceptAllInformative
                  onAcceptAll={handleAcceptAllInformative}
                  testid="do-accept-all-informative"
                />
              </div>
              {/* v1.56 (feb-2026) — Doppia checkbox IVASS pre-submit */}
              <div className="mt-5 pt-5 border-t border-[#E5E2DB]">
                <IVASSComplianceCheckboxes
                  signedDeclaration={signedDeclaration}
                  onSignedDeclarationChange={setSignedDeclaration}
                  mupSeen={mupSeen}
                  onMupSeenChange={setMupSeen}
                  mupFilename={mupFilename}
                  onMupFilenameChange={setMupFilename}
                  testIdPrefix="do"
                />
              </div>
              {/* v1.66 — Consensi granulari GDPR */}
              <div className="mt-4">
                <ConsensiGranulari
                  value={consensi}
                  onChange={setConsensi}
                  testIdPrefix="do"
                  includeSanitario={false}
                />
              </div>
            </div>
          )}

          {/* Submit error summary banner */}
          {!showPreExport && submitErrors && submitErrors.length > 0 && (
            <div className="mb-6 rounded-2xl border-2 border-red-300 bg-red-50 p-5"
                  data-testid="do-wizard-submit-errors-banner"
                  role="alert"
                  aria-live="polite">
              <h3 className="font-heading text-base text-red-800 font-medium mb-1 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 shrink-0" />
                {submitErrors.length === 1
                  ? "1 campo da correggere prima di inviare"
                  : `${submitErrors.length} campi da correggere prima di inviare`}
              </h3>
              <p className="text-xs text-red-600/80 mb-3 ml-7">
                Clicca su un errore per andare direttamente al campo.
              </p>
              <ul className="space-y-2 mt-3">
                {submitErrors.slice(0, 12).map((iss, i) => {
                  const reasonText = iss.fromBackend
                    ? null
                    : (REASON_MESSAGES[iss.reason] || iss.reason);
                  const displayLabel = iss.label || iss.field;
                  return (
                    <li key={i}
                        className="text-sm text-red-700"
                        data-testid={`do-wizard-submit-error-${iss.field}-${i}`}>
                      <button type="button"
                              onClick={() => jumpToField(iss.stepIdx, iss.field)}
                              className="text-left w-full group hover:bg-red-100/60 rounded-lg px-2 py-1.5 -mx-2 transition-colors cursor-pointer"
                              data-testid={`do-wizard-goto-step-${iss.stepIdx + 1}-${iss.field}`}>
                        <span className="inline-flex items-start gap-2">
                          <span className="text-red-400 mt-0.5" aria-hidden="true">•</span>
                          <span className="flex-1">
                            {!iss.fromBackend && (
                              <span className="text-[11px] uppercase tracking-wide text-red-500/80 font-medium block mb-0.5">
                                Step {iss.stepIdx + 1} · {iss.stepTitle}
                              </span>
                            )}
                            <span className="font-semibold text-red-800 group-hover:underline">
                              {displayLabel}
                            </span>
                            {reasonText && (
                              <span className="text-red-600 font-normal"> — {reasonText}</span>
                            )}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
                {submitErrors.length > 12 && (
                  <li className="text-xs text-red-500 italic ml-4">
                    …e altri {submitErrors.length - 12} campi
                  </li>
                )}
              </ul>
            </div>
          )}

          <StepProgress
            steps={orderedSteps.map((s) => s.title)}
            currentStep={stepIdx}
            onStepClick={(i) => { setShowPreExport(false); setCurrentStep(i + 1); }}
            progress={progress}
            testIdPrefix="do-wizard"
          />

          <div className={showPreExport ? "hidden" : "bg-white rounded-2xl border border-[#E5E2DB] p-5 sm:p-7 mb-6 max-w-full overflow-hidden"}>
            {openMode && stepIdx === 0 && (
              <ContactsBlock contactData={contactData} setContactData={setContactData} />
            )}
            {/* FASE 13 — step virtual IVASS */}
            {step && step.virtual && step.key === "__ivass_compliance__" && (
              <IvassConsentsStepUnified
                module="do"
                groupFilter={["colucci_trasversali"]}
                value={ivassBulk}
                onChange={setIvassBulk}
              />
            )}
            {step && !step.virtual && (
              <SectionBlock sezKey={step.sezKey} sez={step.sez}
                             formData={formData} setField={setField} />
            )}
            {/* CyberPromptBlock: mostrato nel penultimo step reale (prima dell'IVASS) */}
            {!step?.virtual && stepIdx === totalSteps - 2 && (
              <CyberPromptBlock wantsCyber={wantsCyber} setWantsCyber={setWantsCyber} />
            )}
          </div>

          {!canGoNext && stepIdx < totalSteps - 1 && !showPreExport && !step?.virtual && (
            <p className="text-xs text-amber-700 mb-3 text-center"
                data-testid="do-wizard-step-invalid">
              Completa i campi obbligatori dello step (contrassegnati da <span className="text-red-600">*</span>) per procedere.
            </p>
          )}
          {step?.virtual && step?.key === "__ivass_compliance__" && !ivassBulk.accepted && !showPreExport && (
            <p className="text-xs text-amber-700 mb-3 text-center"
                data-testid="do-wizard-ivass-required">
              Consulta e conferma la documentazione precontrattuale IVASS per procedere.
            </p>
          )}

          {!showPreExport && (
            <FormNavigation
              step={stepIdx}
              totalSteps={totalSteps}
              onPrev={() => setCurrentStep(Math.max(1, currentStep - 1))}
              onNext={() => canGoNext && setCurrentStep(Math.min(totalSteps, currentStep + 1))}
              onSubmit={handleSubmit}
              submitting={submitting}
              canSubmit={true /* always allow click — summary banner mostra errori */}
              testIdPrefix="do-wizard"
            />
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}

function ThanksPage({ clientName, openMode, wantsCyber, nextCyberUrl, downloads, submissionId }) {
  const [countdown, setCountdown] = React.useState(5);
  React.useEffect(() => {
    if (!wantsCyber || !nextCyberUrl) return undefined;
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(t);
          window.location.href = nextCyberUrl;
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [wantsCyber, nextCyberUrl]);
  const hasDownloads = Array.isArray(downloads) && downloads.length > 0;
  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="do-wizard-thanks">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
          {hasDownloads ? (
            <WizardDownloadPanel
              title="Preventivi D&O generati!"
              downloads={downloads}
              submissionId={submissionId}
              ramoLabel="D&O"
              clientName={clientName} />
          ) : (
            <div className="max-w-md mx-auto text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="font-heading text-3xl text-[#0F1F14] mb-3">Grazie!</h1>
              <p className="text-[#4A5E51] mb-3">
                {clientName ? `${clientName}, abbiamo ricevuto i tuoi dati.` : "Abbiamo ricevuto i tuoi dati."}
              </p>
              <p className="text-sm text-[#4A5E51] mb-4">
                {openMode
                  ? "Colucci Assicuratori esaminer\u00e0 la tua richiesta e ti contatter\u00e0 entro 24 ore. Preventivo non vincolante — l'accettazione definitiva \u00e8 soggetta a valutazione della compagnia."
                  : "Il tuo agente Colucci sta elaborando i questionari. Riceverai un aggiornamento via email."}
              </p>
            </div>
          )}

          {wantsCyber && nextCyberUrl && (
            <div className="mt-6 p-5 rounded-2xl bg-gradient-to-br from-[#D9C49A]/30 to-[#F0E7C8] border border-[#D9C49A]"
                  data-testid="do-wizard-cyber-handoff">
              <Sparkles className="w-6 h-6 text-[#163321] mx-auto mb-2" />
              <h3 className="font-heading text-lg text-[#0F1F14] mb-2 text-center">
                Procedi con il Questionario Cyber Risk
              </h3>
              <p className="text-sm text-[#4A5E51] mb-4 text-center">
                I tuoi dati anagrafici saranno pre-popolati automaticamente.
                Verrai reindirizzato tra <strong>{countdown}</strong> secondi.
              </p>
              <div className="text-center">
                <a href={nextCyberUrl}
                    data-testid="do-wizard-cyber-start-btn"
                    className="inline-block px-5 py-2.5 rounded-full bg-[#163321] text-white text-sm font-medium hover:bg-[#0F1F14] transition-colors">
                  Inizia ora il Questionario Cyber →
                </a>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}

function TransmittedPage({ token, clientName }) {
  const [pdfs, setPdfs] = React.useState([]);
  const tryDownloads = ["proposal_generic", "reale_mutua_5262",
                         "aig_business_guard", "aig_advisors"];
  React.useEffect(() => {
    Promise.all(tryDownloads.map(async (slug) => {
      try { await axios.head(`${BASE}/${token}/artifacts/${slug}`); return slug; }
      catch { return null; }
    })).then((res) => setPdfs(res.filter(Boolean)));
  }, [token]);
  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="do-wizard-transmitted">
        <div className="max-w-md mx-auto px-6 py-16">
          <h1 className="font-heading text-2xl text-[#0F1F14] mb-2 text-center">I tuoi documenti</h1>
          <p className="text-sm text-[#4A5E51] mb-6 text-center">
            {clientName ? `${clientName}, ` : ""}l&apos;agenzia ha completato l&apos;elaborazione.
          </p>
          <div className="space-y-2">
            {pdfs.length === 0 && (
              <p className="text-xs text-slate-400 text-center">Nessun PDF disponibile</p>
            )}
            {pdfs.map((slug) => (
              <a key={slug} href={`${BASE}/${token}/artifacts/${slug}`}
                  target="_blank" rel="noreferrer"
                  data-testid={`do-wizard-dl-${slug}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white border border-[#E5E2DB] hover:border-[#163321]/30 transition-colors">
                <Download className="w-5 h-5 text-[#163321]" />
                <span className="text-sm font-medium text-[#0F1F14]">
                  {slug.replace(/_/g, " ").toUpperCase()}
                </span>
              </a>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function ErrorPage({ errCode }) {
  const messages = {
    EXPIRED: "Questo invito \u00e8 scaduto. Contatta il tuo agente Colucci per riceverne uno nuovo.",
    REVOKED: "Questo invito \u00e8 stato revocato. Contatta il tuo agente Colucci.",
  };
  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid={`do-wizard-error-${errCode}`}>
        <div className="max-w-md mx-auto px-6 py-16 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="font-heading text-xl text-[#0F1F14] mb-2">Impossibile aprire l&apos;invito</h1>
          <p className="text-[#4A5E51] mb-4">
            {messages[errCode] || "Link non valido o non riconosciuto."}
          </p>
          <p className="text-xs text-slate-400">Codice: {errCode}</p>
        </div>
      </main>
      <Footer />
    </>
  );
}
